import React, { useState } from "react";
import {
  RiCloseLine,
  RiDeleteBin6Line,
  RiShoppingCartLine,
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiCalendarLine,
  RiTimeLine,
} from "react-icons/ri";
import { useTranslation } from "../../hooks/useTranslation";
import { useCart } from "../../context/CartContext";
import {
  Modal,
  Button,
  InputNumber,
  DatePicker,
  Divider,
  Tag,
  message,
  Badge,
  Empty,
  Input,
  Form,
  Steps,
  Card,
  Select,
  Space,
  Typography,
} from "antd";
import moment from "moment";
import "./CartModal.css";

const { Step } = Steps;
const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CartModal = ({ open, onClose }) => {
  const translation = useTranslation();
  const {
    cartItems,
    updateCartItem,
    removeFromCart,
    calculateSubtotal,
    calculateTotal,
    clearCart,
  } = useCart();
  const API_BASE_URL = "https://techcrew.pk/ulc/";
  const [activeTab, setActiveTab] = useState("cart");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();

  const handleUpdateRental = (itemId, field, value) => {
    updateCartItem(itemId, { [field]: value });
  };

  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString("en-US", {
      style: "currency",
      currency: "SAR",
      minimumFractionDigits: 2,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("MMM D, YYYY");
  };

  const getDateRangeValue = (startDate, endDate) => {
    try {
      return [
        startDate ? moment(startDate) : null,
        endDate ? moment(endDate) : null,
      ];
    } catch (e) {
      return [null, null];
    }
  };

  const handleCheckout = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const orderData = {
        items: cartItems.map((item) => ({
          equipment_id: item.id,
          quantity: item.quantity,
          rental_type: item.rentalType,
          duration: item.duration,
          start_date: moment(item.startDate).format("YYYY-MM-DD"), // Ensure proper date format
          end_date: moment(item.endDate).format("YYYY-MM-DD"), // Ensure proper date format
          unit_price: item.totalPrice,
          total_price: item.totalPrice * item.quantity,
        })),
        special_notes: values.notes || "",
        new_user: {
          name: values.name,
          email: values.email,
          phone: values.phone,
        },
      };

      console.log("Sending order data:", orderData); // Debug log

      const response = await fetch(`${API_BASE_URL}Checkout.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.status?.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.status.code !== 200) {
        throw new Error(result.status.message);
      }

      message.success("Order placed successfully!");
      clearCart();
      onClose();
      setCurrentStep(0);

      if (values.email) {
        message.info(
          `Account created! Your temporary password has been sent to ${values.email}`
        );
      }
    } catch (error) {
      console.error("Checkout error:", error);
      message.error(
        error.message || "Checkout failed. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 0 && cartItems.length === 0) {
      message.warning("Your cart is empty");
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const renderEmptyCart = () => (
    <div className="empty-cart-container">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary" className="empty-text">
            {translation("cart.empty")}
          </Text>
        }
      >
        <Button
          type="primary"
          onClick={onClose}
          size="large"
          className="browse-btn"
        >
          {translation("cart.browse_equipment")}
        </Button>
      </Empty>
    </div>
  );

  const renderCartItem = (item) => (
    <Card
      key={`${item.id}-${item.rentalType}-${item.startDate}`}
      className="cart-item-card"
      hoverable
    >
      <div className="cart-item-content">
        <div className="item-main-info">
          <div className="item-header">
            <Text strong className="item-name">
              {item.name}
            </Text>
            <div className="item-meta">
              <Tag color="var(--primary-blue)" className="item-category">
                {item.category}
              </Tag>
            </div>
          </div>

          <div className="rental-options">
            <div className="rental-type-selector">
              <Select
                value={item.rentalType}
                onChange={(value) =>
                  handleUpdateRental(item.id, "rentalType", value)
                }
                className="rental-type-select"
                suffixIcon={<RiTimeLine />}
              >
                <Option value="hourly">{translation("cart.hourly")}</Option>
                <Option value="daily">{translation("cart.daily")}</Option>
                <Option value="weekly">{translation("cart.weekly")}</Option>
              </Select>
            </div>

            <div className="duration-control">
              <Text type="secondary" className="duration-label">
                {translation("cart.duration")}
              </Text>
              <Space>
                <InputNumber
                  min={1}
                  value={item.duration}
                  onChange={(value) =>
                    handleUpdateRental(item.id, "duration", value)
                  }
                  className="duration-input"
                />
                <Text type="secondary" className="duration-unit">
                  {item.rentalType === "hourly"
                    ? translation("cart.hours")
                    : item.rentalType === "daily"
                    ? translation("cart.days")
                    : translation("cart.weeks")}
                </Text>
              </Space>
            </div>

            <div className="date-range-control">
              <Text type="secondary" className="date-label">
                {translation("cart.dates")}
              </Text>
              <DatePicker.RangePicker
                value={getDateRangeValue(item.startDate, item.endDate)}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    handleUpdateRental(
                      item.id,
                      "startDate",
                      dates[0].format("YYYY-MM-DD")
                    );
                    handleUpdateRental(
                      item.id,
                      "endDate",
                      dates[1].format("YYYY-MM-DD")
                    );
                  }
                }}
                className="date-range-picker"
                suffixIcon={<RiCalendarLine />}
              />
            </div>
          </div>
        </div>

        <div className="item-price-actions">
          <div className="price-container">
            <Text strong className="item-price">
              {formatPrice(item.totalPrice)}
            </Text>
            {item.discount > 0 && (
              <Tag color="green" className="discount-tag">
                {translation("cart.save")} {item.discount}%
              </Tag>
            )}
          </div>

          <div className="quantity-remove-control">
            <div className="quantity-control">
              <Button
                shape="circle"
                size="small"
                onClick={() =>
                  handleUpdateRental(
                    item.id,
                    "quantity",
                    Math.max(1, item.quantity - 1)
                  )
                }
                className="quantity-btn minus"
              >
                -
              </Button>
              <Text className="quantity-value">{item.quantity}</Text>
              <Button
                shape="circle"
                size="small"
                onClick={() =>
                  handleUpdateRental(item.id, "quantity", item.quantity + 1)
                }
                className="quantity-btn plus"
              >
                +
              </Button>
            </div>
            <Button
              type="text"
              danger
              icon={<RiDeleteBin6Line />}
              onClick={() => {
                message.success(`${item.name} removed from cart`);
                removeFromCart(item.id);
              }}
              className="remove-btn"
            />
          </div>
        </div>
      </div>
    </Card>
  );

  const renderCartContent = () => (
    <div className="cart-body">
      {cartItems.length === 0 ? (
        renderEmptyCart()
      ) : (
        <div className="cart-items-list">{cartItems.map(renderCartItem)}</div>
      )}
    </div>
  );

  const renderSummaryItem = (item) => (
    <Card key={`${item.id}-summary`} className="summary-item-card">
      <div className="summary-item-content">
        <div className="summary-item-info">
          <div className="item-title-row">
            <Text strong className="item-name">
              {item.name}
            </Text>
            <Text className="item-quantity">x {item.quantity}</Text>
          </div>
          <div className="item-details-row">
            <Tag color="var(--primary-blue)" className="rental-tag">
              {item.rentalType} rental
            </Tag>
            <Text type="secondary" className="rental-duration">
              {item.duration}{" "}
              {item.rentalType === "hourly"
                ? translation("cart.hours")
                : item.rentalType === "daily"
                ? translation("cart.days")
                : translation("cart.weeks")}
            </Text>
          </div>
          <div className="item-dates-row">
            <RiCalendarLine className="date-icon" />
            <Text type="secondary" className="date-range">
              {formatDate(item.startDate)} - {formatDate(item.endDate)}
            </Text>
          </div>
        </div>
        <div className="summary-item-price">
          <Text strong className="price-value">
            {formatPrice(item.totalPrice * item.quantity)}
          </Text>
          {item.discount > 0 && (
            <Text type="success" className="discount-text">
              ({translation("cart.save")} {item.discount}%)
            </Text>
          )}
        </div>
      </div>
    </Card>
  );

  const renderSummaryContent = () => (
    <div className="summary-body">
      <Text strong className="summary-title">
        {translation("cart.order_summary")}
      </Text>
      <Divider className="summary-divider" />

      <div className="summary-items-container">
        {cartItems.map(renderSummaryItem)}
      </div>

      <Divider className="summary-divider" />

      <div className="order-totals">
        <div className="total-row">
          <Text className="total-label">{translation("cart.subtotal")}</Text>
          <Text className="total-value">
            {formatPrice(calculateSubtotal())}
          </Text>
        </div>
        <div className="total-row">
          <Text className="total-label">{translation("cart.tax")}</Text>
          <Text className="total-value">
            {formatPrice(calculateTotal() * 0.15)}
          </Text>
        </div>
        <div className="total-row grand-total">
          <Text strong className="total-label">
            {translation("cart.total")}
          </Text>
          <Text strong className="total-value">
            {formatPrice(calculateTotal() * 1.15)}
          </Text>
        </div>
      </div>
    </div>
  );

  const renderCustomerForm = () => (
    <div className="customer-form-container">
      <Text strong className="form-title">
        {translation("cart.customer_info")}
      </Text>
      <Divider className="form-divider" />

      <Form form={form} layout="vertical" className="customer-form">
        <Form.Item
          name="name"
          label={translation("cart.full_name")}
          rules={[{ required: true, message: "Please input your name!" }]}
        >
          <Input
            prefix={<RiUserLine className="input-icon" />}
            placeholder="John Doe"
            className="form-input"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={translation("cart.email")}
          rules={[
            { required: true, message: "Please input your email!" },
            { type: "email", message: "Please enter a valid email!" },
          ]}
        >
          <Input
            prefix={<RiMailLine className="input-icon" />}
            placeholder="john@example.com"
            className="form-input"
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label={translation("cart.phone")}
          rules={[
            { required: true, message: "Please input your phone number!" },
          ]}
        >
          <Input
            prefix={<RiPhoneLine className="input-icon" />}
            placeholder="+966 50 123 4567"
            className="form-input"
          />
        </Form.Item>

        <Form.Item name="notes" label={translation("cart.special_notes")}>
          <TextArea
            rows={3}
            placeholder={translation("cart.notes_placeholder")}
            className="form-textarea"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return activeTab === "cart"
          ? renderCartContent()
          : renderSummaryContent();
      case 1:
        return renderCustomerForm();
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose();
        setCurrentStep(0);
      }}
      footer={null}
      width={900}
      centered
      className="cart-modal-container"
      closeIcon={<RiCloseLine className="close-icon" />}
      styles={{ body: { padding: 0 } }}
    >
      <div className="cart-modal">
        <div className="cart-header">
          <div className="header-content">
            <RiShoppingCartLine className="cart-icon" />
            <Text strong className="cart-title">
              {translation("cart.your_cart")}
            </Text>
            <Badge
              count={cartItems.length}
              showZero
              color="var(--accent-gold)"
              className="cart-badge"
            />
          </div>
        </div>

        <Steps current={currentStep} className="checkout-steps">
          <Step title={translation("cart.review_cart")} />
          <Step title={translation("cart.customer_info")} />
          <Step title={translation("cart.confirmation")} />
        </Steps>

        {currentStep === 0 && (
          <div className="cart-tabs-container">
            <Button
              type={activeTab === "cart" ? "primary" : "text"}
              onClick={() => setActiveTab("cart")}
              className={`tab-button ${activeTab === "cart" ? "active" : ""}`}
            >
              {translation("cart.cart_tab")}
            </Button>
            <Button
              type={activeTab === "summary" ? "primary" : "text"}
              onClick={() => setActiveTab("summary")}
              className={`tab-button ${
                activeTab === "summary" ? "active" : ""
              }`}
            >
              {translation("cart.summary_tab")}
            </Button>
          </div>
        )}

        <div className="cart-content-container">{renderStepContent()}</div>

        {cartItems.length > 0 && (
          <div className="cart-footer">
            <div className="cart-actions">
              {currentStep > 0 ? (
                <Button onClick={prevStep} size="large" className="back-button">
                  {translation("cart.back")}
                </Button>
              ) : (
                <Button
                  onClick={onClose}
                  size="large"
                  className="continue-shopping-btn"
                >
                  {translation("cart.continue_shopping")}
                </Button>
              )}

              {currentStep < 1 ? (
                <Button
                  type="primary"
                  onClick={nextStep}
                  size="large"
                  className="next-step-button"
                  disabled={cartItems.length === 0}
                >
                  {translation("cart.proceed_to_checkout")}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleCheckout}
                  loading={loading}
                  size="large"
                  className="checkout-button"
                >
                  {translation("cart.place_order")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CartModal;
